import React, { useContext } from 'react';
import { defineMessages, t, T } from '@sm/intl';
import { StaticContext } from '@sm/webassets/StaticContext';
import { Button } from '@wds/button';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { generateMetricsAttribute } from '@sm/metrics';
import { createURL } from '@sm/utils';
import { LogoWithText } from '@sm/webassets/Logos';
import { TabletScreen, MobileScreen, DesktopScreen } from '@sm/webassets/MediaComponents';
import classNames from 'classnames';

import useStyles from './useStyles';

const COPY = defineMessages({
  HEADER: {
    id: 'CovidEndpageHero.Header',
    defaultMessage: 'Help those who are helping others',
    description:
      '[Type: Header][vis: high] - The only message on the webpage. Used on a Nonbranded webpage to say thank you to survey respondents and signal the end of the survey.',
  },
  SUBHEADER: {
    id: 'CovidEndpageHero.Subheader',
    defaultMessage:
      'Take a survey to raise $0.50 for organizations like the American Red Cross, International Rescue Committee, Doctors Without Borders, and more.',
    description:
      '[Type: Label][Vis: med] - This figure of speech is used to introduce and display the SurveyMonkey Logo',
  },
  HEADER_CTA: {
    id: 'CovidEndpageHero.HeaderCTA',
    defaultMessage: 'Take a survey',
    description:
      '[Type: Label][Vis: med] - This is the phrase used inside a button that directs views to a signup page',
  },
  LOGO_ARIA_LABEL: {
    id: 'CovidEndpageHero.LogoAriaLabel',
    defaultMessage: 'SurveyMonkey Home',
    description: '[Type: Header][Vis: med] - The SurveyMonkey Logo shown at the top of the page',
  },
});

export type Props = {
  utSource?: string;
  utSource2?: string;
};

const contributeItemSelected = 'contribute sign-up cta';

const CovidEndpageHero = ({ utSource = 'survey-thanks', utSource2 = 'CovidEndpage' }: Props): React.ReactElement => {
  const classes = useStyles();
  const utSource3 = 'CovidEndpageHero';
  const contributeURL = createURL('https://contribute.surveymonkey.com/sign-up/create-account', {
    ut_source: utSource,
    ut_source2: utSource2,
    ut_source3: utSource3,
    ut_ctatext: t(COPY.HEADER_CTA),
  });
  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
  } = useContext(StaticContext);
  const amplitudeEventName = 'endpage user interaction';
  return (
    <>
      <a
        aria-label={t(COPY.LOGO_ARIA_LABEL)}
        data-testid="CovidEndpageHero__Logo"
        href={createURL('/', {
          ut_source: utSource,
          ut_source2: utSource2,
          ut_source3: utSource3,
          ut_ctatext: 'LogoWithText',
        })}
        data-sm-metrics={generateMetricsAttribute({
          data: {
            amplitudeEvent: amplitudeEventName,
            itemSelected: 'logo',
            templateType,
            surveyId,
            collectorId,
            respondentId,
            log: {
              message: `${utSource}-${utSource2}-${utSource3}-LogoWithText`,
            },
          },
        })}
      >
        <LogoWithText className={classes.logo} />
      </a>
      <DesktopScreen>
        <Box display="flex" justifyContent="center" className={classes.wideBackgroundContainer}>
          <Box display="flex" alignItems="center" flexDirection="row" className={classes.heroContainer}>
            <Box className={classes.redHeartGift} ml={3} mt={8} />
            <Box className={classes.heroCopy}>
              <Typography color="dark" component="div" variant="hero1">
                <Box element="span" mt={6} mb={6} className={classes.heroHeader}>
                  <T desc={COPY.HEADER} />
                </Box>
              </Typography>
              <Box mt={5} mb={5} />
              <Typography color="dark" component="div" variant="body">
                <Box element="span" mb={6} className={classes.heroSubHeader}>
                  <T desc={COPY.SUBHEADER} />
                </Box>
              </Typography>
              <Box mt={4} />
              <Button
                size="lg"
                href={contributeURL}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    amplitudeEvent: amplitudeEventName,
                    itemSelected: contributeItemSelected,
                    templateType,
                    surveyId,
                    collectorId,
                    respondentId,
                    log: {
                      message: `${utSource}-${utSource2}-${utSource3}-${COPY.HEADER_CTA.defaultMessage}`,
                    },
                  },
                })}
              >
                <T desc={COPY.HEADER_CTA} />
              </Button>
            </Box>
          </Box>
        </Box>
      </DesktopScreen>
      <TabletScreen>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          className={classes.slimBackgroundContainer}
        >
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            mt={8}
            mb={7}
            className={classNames(classes.heroCopy, classes.heroContainer)}
          >
            <Typography color="dark" component="div" variant="hero1" align="center">
              <Box element="span" mb={6} className={classes.heroHeader}>
                <T desc={COPY.HEADER} />
              </Box>
            </Typography>
            <Box mt={4} mb={4} />
            <Typography color="dark" component="div" variant="body" align="center">
              <Box element="span" mb={6} className={classes.heroSubHeader}>
                <T desc={COPY.SUBHEADER} />
              </Box>
            </Typography>
            <Box mt={6} display="flex" flexJustify="center">
              <Button
                size="lg"
                href={contributeURL}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    amplitudeEvent: amplitudeEventName,
                    itemSelected: contributeItemSelected,
                    templateType,
                    surveyId,
                    collectorId,
                    respondentId,
                    log: {
                      message: `${utSource}-${utSource2}-${utSource3}-${COPY.HEADER_CTA.defaultMessage}`,
                    },
                  },
                })}
              >
                <T desc={COPY.HEADER_CTA} />
              </Button>
            </Box>
          </Box>
          <Box className={classes.redHeartGift} mt={9} />
        </Box>
      </TabletScreen>
      <MobileScreen>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          className={classes.slimBackgroundContainer}
        >
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            mt={8}
            mb={7}
            className={classNames(classes.heroCopy, classes.heroContainer)}
          >
            <Typography color="dark" component="div" variant="hero1" align="center">
              <Box element="span" mb={6} className={classes.heroHeader}>
                <T desc={COPY.HEADER} />
              </Box>
            </Typography>
            <Box mt={4} mb={4} />
            <Typography color="dark" component="div" variant="body" align="center">
              <Box element="span" mb={6} className={classes.heroSubHeader}>
                <T desc={COPY.SUBHEADER} />
              </Box>
            </Typography>
            <Box mt={4} display="flex" flexJustify="center">
              <Button
                size="lg"
                href={contributeURL}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    amplitudeEvent: amplitudeEventName,
                    itemSelected: contributeItemSelected,
                    templateType,
                    surveyId,
                    collectorId,
                    respondentId,
                    log: {
                      message: `${utSource}-${utSource2}-${utSource3}-${COPY.HEADER_CTA.defaultMessage}`,
                    },
                  },
                })}
              >
                <T desc={COPY.HEADER_CTA} />
              </Button>
            </Box>
          </Box>
          <Box className={classes.redHeartGift}>
            <></>
          </Box>
        </Box>
      </MobileScreen>
    </>
  );
};

export default CovidEndpageHero;
